import { Select, Table, TableColumn, TableProps } from 'oskcomponents';
import { TaskingOrder, TaskingOrderStatus } from 'oskcore/src/api/orders';
import React from 'react';
import styled from 'styled-components';
import { DEFAULT_ORDER_STATUS_STYLE, OrderStatusMapEntry, TaskingOrderStatusMap } from '~/redux/modules/data/orders';
import { date_format_longer } from '~/utils';
import { PendingOrderStatusEdit } from '.';

const StyledTable = styled<any>(Table)`
    & {
        margin-top: 18px;
        border: 2px solid ${(props: any) => props.theme.colors[props.variant ?? 'primary'].bg};
    }

    tbody {
        tr {
            td {
                background-color: white;
            }
        }
    }
    tbody tr:nth-child(even) {
        td {
            background-color: white;
        }
    }

    tbody tr:hover {
        td {
            background-color: ${(props: any) => props.theme.colors[props.variant ?? 'primary'].highlight} !important;
        }
    }
`;

type OrdersAdminTableProps = {
    /** List of orders to display */
    orders: TaskingOrder[];
    /** Whether or not to display relevant columns in edit mode */
    enableEdit: boolean;
    /** A list of pending edits to order status, used to override the displayed value of an order's status */
    pendingEdits?: PendingOrderStatusEdit[];
    /** A method that's called when a new status is given for an order */
    onStatusChange: (orderId: string, newStatusCode: string) => void;
} & Omit<TableProps, 'data' | 'columns' | 'style'>;

const OrdersAdminTable = ({ orders, enableEdit, pendingEdits, onStatusChange }: OrdersAdminTableProps) => {
    const columns = React.useMemo<Array<TableColumn>>(
        () => [
            { Header: 'ID', accessor: 'id', disableSortBy: true },
            {
                Header: 'Order Name',
                accessor: 'properties.order_parameters.name',
                disableSortBy: true,
            },
            {
                Header: 'Order Description',
                accessor: 'properties.order_parameters.description',
                disableSortBy: true,
            },
            {
                Header: 'Order Date',
                accessor: 'properties.created',
                disableSortBy: true,
                Cell: ({ value }: any) => {
                    return date_format_longer(value);
                },
            },
            {
                Header: 'Status',
                accessor: 'properties.status',
                disableSortBy: true,
                width: '250px',
                Cell: ({ value, row }: any) => {
                    const status = value as TaskingOrderStatus;
                    const { id } = row.original;
                    const pendingEdit = pendingEdits?.find((edit) => edit.id === id);
                    let StatusDisplayStyle: OrderStatusMapEntry = DEFAULT_ORDER_STATUS_STYLE;
                    if (status.status_code in TaskingOrderStatusMap) {
                        StatusDisplayStyle = TaskingOrderStatusMap[status.status_code];
                    } else {
                        StatusDisplayStyle.name = status.status_code;
                    }

                    return (
                        <Select
                            name=""
                            variant="contrast"
                            items={[status.status_code, 'delivered', 'failed'].map((status) => ({
                                label: status in TaskingOrderStatusMap ? TaskingOrderStatusMap[status].name : status,
                                value: status,
                            }))}
                            defaultValue={pendingEdit?.status ?? status.status_code}
                            displayOnly={!enableEdit}
                            onSelect={(e) => {
                                onStatusChange(id, e?.value);
                            }}
                        />
                    );
                },
            },
        ],
        [enableEdit, pendingEdits],
    );

    return <StyledTable columns={columns} data={orders} variant="neutral" />;
};

export default OrdersAdminTable;
